import { NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import {
  Shield,
  Building2,
  Users,
  MapPin,
  Route,
  ClipboardList,
  DollarSign,
  LogOut,
  LayoutDashboard,
  Menu,
  X,
  UserCheck,
  Radio,
  Calendar,
  Settings,
} from "lucide-react";
import { Button } from "../ui/button";
import { useState } from "react";
import { cn } from "../../lib/utils";
import { LanguageSelector } from "../LanguageSelector";

export const Sidebar = () => {
  const { user, logout, isAdmin, canManageCompanies, canManage } = useAuth();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const navItems = [
    {
      to: "/dashboard",
      icon: LayoutDashboard,
      label: t("nav.dashboard"),
      show: true,
    },
    {
      to: "/companies",
      icon: Building2,
      label: t("nav.companies"),
      show: canManageCompanies(), // Admin y Revendedor ven compañías
    },
    {
      to: "/users",
      icon: Users,
      label: t("nav.users"),
      show: canManage(),
    },
    {
      to: "/checkpoints",
      icon: MapPin,
      label: t("nav.checkpoints"),
      show: canManage(),
    },
    {
      to: "/routes",
      icon: Route,
      label: t("nav.routes"),
      show: canManage(),
    },
    {
      to: "/patrol-logs",
      icon: ClipboardList,
      label: t("nav.patrolLogs"),
      show: canManage(),
    },
    {
      to: "/guard-records",
      icon: UserCheck,
      label: t("nav.guardRecords"),
      show: canManage(),
    },
    {
      to: "/active-guards",
      icon: Radio,
      label: t("nav.activeGuards"),
      show: canManage(),
    },
    {
      to: "/payroll",
      icon: DollarSign,
      label: t("nav.payroll"),
      show: canManage(),
    },
    {
      to: "/holidays",
      icon: Calendar,
      label: t("nav.holidays"),
      show: canManage(),
    },
    {
      to: "/company-settings",
      icon: Settings,
      label: t("nav.settings"),
      show: canManage(),
    },
  ];

  const filteredNavItems = navItems.filter((item) => item.show);

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 lg:hidden"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="mobile-menu-toggle"
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </Button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full w-[280px] sidebar-gradient border-r border-slate-200 z-50 transition-transform duration-300 lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-900">
                  Sentinel Pro
                </h1>
                <p className="text-xs text-slate-500">{t("app.securityManagement")}</p>
              </div>
            </div>
            <div className="mt-4">
              <LanguageSelector />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setIsOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors",
                    isActive
                      ? "bg-primary text-white"
                      : "text-slate-600 hover:bg-slate-100"
                  )
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-slate-600">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              {t("nav.logout")}
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};
